import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Plus, X, Search } from 'lucide-react';
import {
  useSchedule,
  useCreateSchedule,
  useUpdateSchedule,
} from '@/hooks/reporting/useSchedules';
import ReportTypeSelector from '@/components/reports/filters/ReportTypeSelector';
import { userManagementV2Service, type UserV2 } from '@/services/userManagementV2.service';
import type { ReportType, FrequencyType, CreateScheduleRequest } from '@/types/reporting-v2';

const FREQUENCY_OPTIONS: { value: FrequencyType; label: string }[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const PERIOD_TYPE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'US Eastern Time' },
  { value: 'America/Chicago', label: 'US Central Time' },
  { value: 'America/Denver', label: 'US Mountain Time' },
  { value: 'America/Los_Angeles', label: 'US Pacific Time' },
  { value: 'Europe/London', label: 'UK Time' },
  { value: 'Europe/Paris', label: 'Central European Time' },
  { value: 'Asia/Tokyo', label: 'Japan Time' },
  { value: 'Asia/Shanghai', label: 'China Time' },
  { value: 'Asia/Kolkata', label: 'India Time' },
];

export default function ScheduleForm() {
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const navigate = useNavigate();
  const isEdit = !!scheduleId;

  const { data: existingSchedule, isLoading: loadingSchedule } = useSchedule(
    isEdit ? scheduleId : undefined
  );
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();

  const [formData, setFormData] = useState<CreateScheduleRequest>({
    schedule_name: '',
    description: '',
    report_type: 'organization_overview',
    frequency: 'weekly',
    is_active: true,
    report_params: {},
    schedule_config: {
      period_type: 'monthly',
      hour: 9,
      minute: 0,
      timezone: 'UTC',
      lookback_periods: 1,
    },
    notifications: {
      on_success: true,
      on_failure: true,
      recipients: [],
    },
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserV2[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (existingSchedule) {
      const existingData = existingSchedule as any;
      setFormData({
        schedule_name: existingSchedule.schedule_name,
        description: existingSchedule.description || '',
        report_type: existingSchedule.report_type,
        frequency: existingSchedule.frequency,
        is_active: existingSchedule.is_active,
        report_params: existingSchedule.report_params || {},
        schedule_config: existingData.schedule_config || {
          period_type: 'monthly',
          hour: 9,
          minute: 0,
          timezone: 'UTC',
          lookback_periods: 1,
        },
        notifications: existingSchedule.notifications || {
          on_success: true,
          on_failure: true,
          recipients: [],
        },
      });
    }
  }, [existingSchedule]);

  // Search users with debouncing
  useEffect(() => {
    const searchUsers = async () => {
      if (searchTerm.trim().length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await userManagementV2Service.getUsersList({
          search: searchTerm,
          status: 'ACTIVE',
          limit: 20,
        });

        // Filter users with admin roles
        const adminUsers = response.data.data.filter((user) =>
          user.groups?.some((group) =>
            group.roles?.some((role) =>
              role.role_name.toLowerCase().includes('admin')
            )
          )
        );

        setSearchResults(adminUsers);
        setShowDropdown(adminUsers.length > 0);
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleAddRecipient = (user: UserV2) => {
    const currentRecipients = formData.notifications?.recipients || [];
    if (!currentRecipients.includes(user.email)) {
      setFormData((prev) => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          recipients: [...currentRecipients, user.email],
        },
      }));
    }
    setSearchTerm('');
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleRemoveRecipient = (email: string) => {
    setFormData((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        recipients: (prev.notifications?.recipients || []).filter((r) => r !== email),
      },
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-search-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (isEdit) {
        await updateSchedule.mutateAsync({
          scheduleId: scheduleId!,
          params: formData,
        });
      } else {
        await createSchedule.mutateAsync(formData);
      }
      navigate('/admin/reporting/schedules');
    } catch (error) {
      console.error('Error saving schedule:', error);
    }
  };

  if (isEdit && loadingSchedule) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/reporting/schedules')}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Schedule' : 'Create Schedule'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isEdit
              ? 'Update automated report generation schedule'
              : 'Set up automated report generation'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Name *
              </label>
              <input
                type="text"
                value={formData.schedule_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, schedule_name: e.target.value }))
                }
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter schedule name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter schedule description (optional)"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, is_active: e.target.checked }))
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active (schedule will run automatically)
              </label>
            </div>
          </div>
        </div>

        {/* Report Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Type *
              </label>
              <ReportTypeSelector
                value={formData.report_type}
                onChange={(type) =>
                  setFormData((prev) => ({ ...prev, report_type: type }))
                }
                variant="dropdown"
              />
            </div>

            {/* Report Data Coverage */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Report Data Coverage</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-blue-900">
                  <strong>Note:</strong> Schedule Frequency controls when the report runs.
                  Report Data Period controls what time period the report covers.
                  <br />
                  <span className="text-blue-700 mt-1 block">
                    Example: A "Weekly" schedule running on Mondays can generate a "Monthly" report covering the last month's data.
                  </span>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Data Period *
                  </label>
                  <select
                    value={formData.schedule_config?.period_type ?? 'monthly'}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        schedule_config: {
                          ...prev.schedule_config!,
                          period_type: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {PERIOD_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    What time range should the report data cover?
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Periods *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={formData.schedule_config?.lookback_periods ?? 1}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        schedule_config: {
                          ...prev.schedule_config!,
                          lookback_periods: parseInt(e.target.value),
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How many periods to include? (e.g., 1 = last {formData.schedule_config?.period_type || 'month'}, 3 = last 3 {formData.schedule_config?.period_type || 'months'})
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Schedule Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Execution Schedule</h2>
          <div className="space-y-4">
            {/* Schedule Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Frequency *
              </label>
              <select
                value={formData.frequency}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    frequency: e.target.value as FrequencyType,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                How often should this report be generated automatically?
              </p>
            </div>

            {/* Time Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hour (0-23) *
                </label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={formData.schedule_config?.hour ?? 9}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config!,
                        hour: parseInt(e.target.value),
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minute (0-59) *
                </label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={formData.schedule_config?.minute ?? 0}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config!,
                        minute: parseInt(e.target.value),
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone *
                </label>
                <select
                  value={formData.schedule_config?.timezone ?? 'UTC'}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config!,
                        timezone: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TIMEZONE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Day Selection for Weekly/Monthly */}
            {formData.frequency === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Week *
                </label>
                <select
                  value={formData.schedule_config?.day_of_week ?? 'monday'}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config!,
                        day_of_week: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {DAY_OF_WEEK_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formData.frequency === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Month (1-31) *
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.schedule_config?.day_of_month ?? 1}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config!,
                        day_of_month: parseInt(e.target.value),
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Notification Recipients */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification Recipients</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search and Add Admin Users
              </label>
              <div className="relative user-search-container">
                <div className="flex gap-2 mb-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Search by name or email (min 2 characters)..."
                      name="user-search-no-autofill"
                      autoComplete="new-password"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      data-form-type="other"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Dropdown Results */}
                {showDropdown && searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.email}
                        type="button"
                        onClick={() => handleAddRecipient(user)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name || `${user.given_name} ${user.family_name}`}
                          </div>
                          <div className="text-xs text-gray-500">{user.email}</div>
                          <div className="flex gap-1 mt-1">
                            {user.groups?.map((group) => (
                              <span
                                key={group.group_key}
                                className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded"
                              >
                                {group.group_name}
                              </span>
                            ))}
                          </div>
                        </div>
                        <Plus className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}

                {showDropdown && !isSearching && searchTerm.length >= 2 && searchResults.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 text-center text-sm text-gray-500">
                    No admin users found matching "{searchTerm}"
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Only active users with admin permissions are shown. These users will receive email notifications when the scheduled report is generated.
              </p>
            </div>

            {formData.notifications?.recipients && formData.notifications.recipients.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipients ({formData.notifications.recipients.length})
                </label>
                <div className="flex flex-wrap gap-2">
                  {formData.notifications.recipients.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-md text-sm"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(email)}
                        className="hover:text-blue-900"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notify_on_success"
                  checked={formData.notifications?.on_success ?? true}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      notifications: {
                        ...prev.notifications,
                        on_success: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="notify_on_success" className="text-sm font-medium text-gray-700">
                  Send notification on successful execution
                </label>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="notify_on_failure"
                  checked={formData.notifications?.on_failure ?? true}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      notifications: {
                        ...prev.notifications,
                        on_failure: e.target.checked,
                      },
                    }))
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="notify_on_failure" className="text-sm font-medium text-gray-700">
                  Send notification on failed execution
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/reporting/schedules')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createSchedule.isPending || updateSchedule.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {createSchedule.isPending || updateSchedule.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                {isEdit ? 'Update Schedule' : 'Create Schedule'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
